import { css } from "@emotion/css";
import { RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectSummaryResource } from "@octopusdeploy/octopus-server-client";
import { useEffect } from "react";
type ProjectSelectionViewProps = {
    useExistingProject: boolean;
    onUseExistingProjectChange: (useExisting: boolean) => void;
    selectedProjectSlug: string | undefined;
    onProjectChange: (slug: string | undefined) => void;
};
export const ProjectSelectionView = (props: ProjectSelectionViewProps) => {
    const { useExistingProject, onUseExistingProjectChange, selectedProjectSlug, onProjectChange } = props;
    const { result: projects, isLoading } = useInlineStatusQuery(async (repo) => await repo.Projects.summaries(), [], "Fetch projects");
    const projectItems = projects?.map((project: ProjectSummaryResource) => ({ value: project.Slug, text: project.Name })) ?? [];
    const hasProjects = !isLoading && projectItems.length > 0;
    // Auto-select "create new project" when there are no existing projects
    useEffect(() => {
        if (!isLoading && projectItems.length === 0 && useExistingProject) {
            onUseExistingProjectChange(false);
        }
    }, [isLoading, projectItems.length, useExistingProject, onUseExistingProjectChange]);
    const handleModeChange = (value: string) => {
        const useExisting = value === "existing";
        onUseExistingProjectChange(useExisting);
        if (!useExisting) {
            onProjectChange(undefined);
        }
    };
    const handleProjectChange = (value: string | undefined) => onProjectChange(value);
    return (<div className={styles.container}>
            <p className={styles.description}>To deploy an application with this Argo CD instance, you'll need to add an Argo CD step to an Octopus project.</p>

            <div className={styles.radioSection}>
                <RadioButtonGroup value={useExistingProject ? "existing" : "new"} onChange={handleModeChange} title="Do you already have an Octopus project you want to use?">
                    <RadioButton value="existing" label="Yes, I have a project" disabled={!hasProjects}/>
                    <RadioButton value="new" label="No, help me create a new project"/>
                </RadioButtonGroup>
            </div>

            {useExistingProject && (<div className={styles.projectSelect}>
                    <DeprecatedSelect label="Project" value={selectedProjectSlug} onChange={handleProjectChange} items={projectItems} placeholder={isLoading ? "Loading projects..." : "Select a project"} allowFilter={true} disabled={isLoading}/>
                </div>)}
        </div>);
};
const styles = {
    container: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    description: css({
        font: text.body.regular.large,
        color: themeTokens.color.text.primary,
        margin: 0,
    }),
    radioSection: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    projectSelect: css({
        maxWidth: "400px",
    }),
};
